// Shared job status controller, design §5: "The shared status controller
// serves jobs started by Reclassify, merchant apply, and rule commit." A
// module-level store (not component state) so `registerJob` is callable
// from anywhere -- a mutation `onSuccess`, a dialog, another hook -- and
// every mounted consumer (CategorizationTools, JobStatusRow, a progress
// dialog) sees the same deduplicated id list without prop drilling.
//
// Persistence is "returned job IDs only" under an authenticated-user-scoped
// sessionStorage key (§5): never transaction text, never credentials. IDs
// hydrate once per signed-in user and clear when `useMe` stops resolving
// that user (logout / auth expiry) -- §5 "clear on logout; revalidate
// ownership via GET" is satisfied by re-polling each id through the
// existing `useJob`, which is itself an authenticated GET.
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useMe } from '@/api/hooks/useMe';
import { isErrorEnvelope } from '@/api/envelope';
import { useJob, type JobStatus } from '@/api/hooks/useJob';
import { viewportBandForWidth } from '@/app/navigation/viewportBand';
import {
  emitReclassifyComplete,
  emitReclassifyTrackingError,
  newInteractionId,
  toErrorClass,
  type TelemetryContext,
  type ViewportBand,
} from '../telemetry';

/**
 * `JobWatcherMount` (`JobRegistryProvider.tsx`) sits above the shell's
 * viewport-band context, so telemetry here computes its own band from the
 * live window width rather than depending on `useShellContext`.
 */
function telemetryContext(): TelemetryContext {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const band = viewportBandForWidth(width);
  const viewport_band: ViewportBand = band === 'mobile' ? 'phone' : band;
  return { surface: 'tools', viewport_band, input_method: 'unknown' };
}

const STORAGE_PREFIX = 'ms.tools.jobIds.';
const SETTLED_STATUSES: JobStatus[] = ['done', 'failed', 'cancelled'];

export type JobOrigin = 'tools' | 'merchant_apply' | 'rule_commit';

let jobIds: string[] = [];
let currentUserId: string | null = null;
const listeners = new Set<() => void>();
// Guards terminal-state cache invalidation so a job is invalidated exactly
// once even though multiple components may observe the same settled job.
const invalidatedJobIds = new Set<string>();
// A dismissed job stops rendering (dismissJob below) but keeps being watched
// -- and keeps invalidating caches on its own terminal transition -- until it
// settles (§5: "Dismissal hides presentation, not the server job.").
let dismissedJobIds = new Set<string>();
// ENG-F-004: ids confirmed 404/`not_found` -- these will never reach a
// SETTLED_STATUSES transition, so `dismissJob` must forget them immediately
// (like an already-invalidated terminal job) instead of leaving them in
// `dismissedJobIds` forever with a watcher that polls a dead id.
const notFoundJobIds = new Set<string>();
// §9 transactions_reclassify_complete origin/duration_ms: which caller
// registered the job, and when, so the terminal event can report them
// honestly instead of a hardcoded null.
const jobOrigins = new Map<string, JobOrigin | null>();
const jobStartedAt = new Map<string, number>();

/**
 * Shared count validator (§5/§9): a progress field counts only if it is a
 * finite, non-negative integer. `JobStatusRow` and the `reclassify_complete`
 * telemetry emitter both call this one function so a count the UI trusts is
 * also a count telemetry trusts, and vice versa (DATA-R1-002).
 */
export function parseCountField(progress: Record<string, unknown> | null, field: string): number | null {
  if (!progress) return null;
  const value = progress[field];
  if (typeof value !== 'number') return null;
  if (!Number.isFinite(value) || !Number.isInteger(value) || value < 0) return null;
  return value;
}

/**
 * §9 `counts_status`: derived from how many of the three progress counts
 * actually parsed, not from whether a `progress` object merely exists (a
 * queued or early-failed job carries `progress = {}`, which is truthy but
 * has no usable counts) -- DATA-R1-001.
 */
export function countsStatus(
  processed: number | null,
  candidateCount: number | null,
  changed: number | null,
): 'known' | 'partial' | 'unavailable' {
  const presentCount = [processed, candidateCount, changed].filter((value) => value !== null).length;
  if (presentCount === 3) return 'known';
  if (presentCount === 0) return 'unavailable';
  return 'partial';
}

function emit(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): string[] {
  return jobIds;
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function persist(): void {
  if (!currentUserId) return;
  try {
    sessionStorage.setItem(storageKey(currentUserId), JSON.stringify(jobIds));
  } catch {
    // sessionStorage unavailable (private mode, quota) -- registry keeps
    // working in-memory for this tab; only reload-recovery is lost.
  }
}

/** Adds a job id to the shared registry (dedupes); safe to call from anywhere, not a hook. */
export function registerJob(jobId: string, origin: JobOrigin | null = null): void {
  if (!jobId || jobIds.includes(jobId)) return;
  jobIds = [...jobIds, jobId];
  jobOrigins.set(jobId, origin);
  jobStartedAt.set(jobId, Date.now());
  emit();
  persist();
}

function forgetJob(jobId: string): void {
  jobIds = jobIds.filter((id) => id !== jobId);
  invalidatedJobIds.delete(jobId);
  dismissedJobIds.delete(jobId);
  notFoundJobIds.delete(jobId);
  jobOrigins.delete(jobId);
  jobStartedAt.delete(jobId);
}

/**
 * Hides a job's status row. The server job is untouched, and if it has not
 * yet reached a settled status the registry keeps polling it in the
 * background so terminal cache invalidation still fires (§5: "Dismissal
 * hides presentation, not the server job."). Once a dismissed job's watcher
 * observes a terminal status (see `useJobWatcher` below), it is fully
 * forgotten -- there is nothing left to watch or invalidate.
 */
export function dismissJob(jobId: string): void {
  if (!jobIds.includes(jobId)) return;
  if (invalidatedJobIds.has(jobId) || notFoundJobIds.has(jobId)) {
    // Already reached a terminal status and already invalidated caches, OR
    // confirmed 404/not_found and will never settle at all (ENG-F-004) --
    // nothing left to watch, safe to drop entirely.
    forgetJob(jobId);
  } else {
    dismissedJobIds = new Set(dismissedJobIds).add(jobId);
    // `jobIds` itself is unchanged (the id is still tracked/watched), but
    // `useSyncExternalStore` bails out of re-rendering when `getSnapshot()`
    // returns the same reference as last time -- rebuild the array so
    // `useTrackedJobs` re-derives `activeJob` against the new dismissed set.
    jobIds = [...jobIds];
  }
  emit();
  persist();
}

/** True while a job is dismissed (hidden from status UI) but still being watched. */
export function isJobDismissed(jobId: string): boolean {
  return dismissedJobIds.has(jobId);
}

/** Clears every tracked id -- called on sign-out so no id survives to the next signed-in user. */
export function clearTrackedJobs(): void {
  currentUserId = null;
  jobIds = [];
  dismissedJobIds = new Set();
  invalidatedJobIds.clear();
  notFoundJobIds.clear();
  jobOrigins.clear();
  jobStartedAt.clear();
  emit();
}

function hydrateForUser(userId: string): void {
  if (currentUserId === userId) return;
  currentUserId = userId;
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    jobIds = Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    jobIds = [];
  }
  emit();
}

/**
 * Keeps `jobIds` scoped to the currently authenticated user: hydrates from
 * sessionStorage once per user id, clears when signed out. Mount once, at
 * app root (`JobRegistryProvider` below) so it survives navigation across
 * Transactions/Accounts/Business/Ask.
 */
export function useJobRegistryUserSync(): void {
  const me = useMe();
  const userId = me.data?.user_id ?? null;
  useEffect(() => {
    if (userId) hydrateForUser(userId);
    else clearTrackedJobs();
  }, [userId]);
}

export interface TrackedJob {
  jobId: string;
  status: JobStatus | null;
  progress: Record<string, unknown> | null;
  error: string | null;
  /** True once the first `useJob` fetch has resolved (settled or not). */
  hasData: boolean;
  /** True when the GET itself is failing -- "Can't check progress" (§5 Polling unavailable). */
  pollError: boolean;
  /** True on a confirmed 404/`not_found` -- distinct from a transient poll failure (§5 Missing/deleted job). */
  notFound: boolean;
  /** Forces an immediate re-poll -- backs the "Check again" affordance (§5 Polling unavailable). */
  refetch: () => void;
}

function isNotFoundError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.response?.status === 404) return true;
  const payload = error.response?.data;
  return isErrorEnvelope(payload) && payload.error.code === 'not_found';
}

function toTrackedJob(jobId: string, job: ReturnType<typeof useJob>): TrackedJob {
  const notFound = job.isError && isNotFoundError(job.error);
  return {
    jobId,
    status: job.data?.status ?? null,
    progress: job.data?.progress ?? null,
    error: job.data?.error ?? null,
    hasData: job.data !== undefined,
    pollError: job.isError && !notFound,
    notFound,
    refetch: () => void job.refetch(),
  };
}

/**
 * One id's polling + terminal invalidation. Used by `JobWatcherMount`
 * (`JobRegistryProvider.tsx`) so invalidation runs centrally regardless of
 * which consumer (if any) is mounted when the job settles -- §5 "On
 * terminal state, refresh Transactions (including detail), Inbox,
 * spend-by-category, and spend-by-merchant queries."
 */
export function useJobWatcher(jobId: string): void {
  const job = useJob(jobId);
  const queryClient = useQueryClient();
  const status = job.data?.status;
  const lastKnownStatus = useRef<string | null>(null);
  useEffect(() => {
    if (status) lastKnownStatus.current = status;
  }, [status]);

  useEffect(() => {
    if (!status || !SETTLED_STATUSES.includes(status)) return;
    if (invalidatedJobIds.has(jobId)) return;
    invalidatedJobIds.add(jobId);
    queryClient.invalidateQueries({ queryKey: ['transactions'] });
    queryClient.invalidateQueries({ queryKey: ['inbox'] });
    queryClient.invalidateQueries({ queryKey: ['spend-by-category'] });
    queryClient.invalidateQueries({ queryKey: ['spend-by-merchant'] });
    // §9 transactions_reclassify_complete: first observed terminal state,
    // deduped by (job_id, status) inside the telemetry module itself.
    const progress = job.data?.progress ?? null;
    const parsedProcessed = parseCountField(progress, 'processed');
    const parsedCandidateCount = parseCountField(progress, 'candidate_count');
    const parsedChanged = parseCountField(progress, 'changed');
    const validRange = parsedProcessed === null || parsedCandidateCount === null || parsedProcessed <= parsedCandidateCount;
    const startedAt = jobStartedAt.get(jobId) ?? null;
    emitReclassifyComplete(telemetryContext(), newInteractionId(), {
      job_id: jobId,
      outcome: status as 'done' | 'failed' | 'cancelled',
      processed: validRange ? parsedProcessed : null,
      candidate_count: validRange ? parsedCandidateCount : null,
      changed: parsedChanged,
      counts_status: countsStatus(validRange ? parsedProcessed : null, validRange ? parsedCandidateCount : null, parsedChanged),
      origin: jobOrigins.get(jobId) ?? null,
      duration_ms: startedAt !== null ? Date.now() - startedAt : null,
      error_class: status === 'failed' ? toErrorClass(job.error) : null,
    });
    // A dismissed job has nothing left to watch once it has settled and its
    // caches have been invalidated -- forget it so it does not linger forever.
    if (dismissedJobIds.has(jobId)) {
      forgetJob(jobId);
      emit();
      persist();
    }
  }, [jobId, status, queryClient, job.data?.progress, job.error]);

  // ENG-F-004: a confirmed 404/not_found job never reaches SETTLED_STATUSES
  // above, so without this it stays in `dismissedJobIds` (and keeps being
  // "watched") forever. Track it separately; if it was already dismissed
  // before this was discovered, forget it now instead of waiting for a
  // terminal status that will never come.
  const notFound = job.isError && isNotFoundError(job.error);
  useEffect(() => {
    if (!notFound) return;
    if (notFoundJobIds.has(jobId)) return;
    notFoundJobIds.add(jobId);
    if (dismissedJobIds.has(jobId)) {
      forgetJob(jobId);
      emit();
      persist();
    }
  }, [jobId, notFound]);

  // §9 transactions_reclassify_tracking_error: polling itself is failing
  // (distinct from a settled terminal status) -- never reported as a
  // terminal-complete event.
  const pollFailing = job.isError && !isNotFoundError(job.error);
  useEffect(() => {
    if (!pollFailing) return;
    emitReclassifyTrackingError(telemetryContext(), newInteractionId(), {
      job_id: jobId,
      last_known_status: lastKnownStatus.current,
      error_class: toErrorClass(job.error),
    });
    // Only once per failed-poll transition, not every re-render while it stays failing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, pollFailing]);
}

/** Read the current tracked id list without subscribing to job data -- used by the provider to mount watchers. */
export function useTrackedJobIds(): string[] {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Read-only view for UI consumers (`CategorizationTools`, `JobStatusRow`,
 * progress dialogs). `activeJob` is the most recently registered id still
 * tracked -- the newest job "wins" the compact status row, matching the
 * single-visible-surface pattern used for the merchant-offer tray (§4.2).
 */
export function useTrackedJobs(): { jobIds: string[]; activeJob: TrackedJob | null } {
  const ids = useSyncExternalStore(subscribe, getSnapshot);
  // A dismissed job is still tracked (still watched, still invalidates on
  // terminal state -- §5), but it must not resurface in the status row, so
  // the "most recent" pick skips dismissed ids.
  const visibleIds = ids.filter((id) => !dismissedJobIds.has(id));
  const activeId = visibleIds.length > 0 ? visibleIds[visibleIds.length - 1] : null;
  const activeJobQuery = useJob(activeId);
  const activeJob = useMemo(
    () => (activeId ? toTrackedJob(activeId, activeJobQuery) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeJobQuery is a fresh object per render; only its fields matter.
    [activeId, activeJobQuery.data, activeJobQuery.isError],
  );
  return { jobIds: ids, activeJob };
}
